type WalletState = {
  connected: boolean;
  publicKey: string | null;
  name: string | null;
  icon: string | null;
};

type LoadingState = {
  wallet: boolean;
  nfts: boolean;
};

type RouteState = {
  path: string;
  params: string[];
  raw: string;
  fullPath?: string;
};

type AppState = {
  wallet: WalletState;
  nfts: unknown[];
  loading: LoadingState;
  route?: RouteState;
};

const initialState: AppState = {
  wallet: {
    connected: false,
    publicKey: null,
    name: null,
    icon: null,
  },
  nfts: [],
  loading: {
    wallet: false,
    nfts: false,
  },
};

class Store extends EventTarget {
  private _state: AppState = structuredClone(initialState);

  get<K extends keyof AppState>(key: K): AppState[K] {
    return this._state[key];
  }

  set<K extends keyof AppState>(key: K, value: AppState[K]): void {
    this._state[key] = value;
    this.dispatchEvent(new CustomEvent(`change:${String(key)}`, { detail: value }));
    this.dispatchEvent(new CustomEvent('change', { detail: { key, value } }));
  }

  merge<K extends keyof AppState>(key: K, partial: Partial<AppState[K]>): void {
    const current = this._state[key];
    if (typeof current === 'object' && current !== null) {
      this.set(key, { ...(current as object), ...(partial as object) } as AppState[K]);
      return;
    }
    this.set(key, partial as AppState[K]);
  }

  on<T = unknown>(event: string, callback: (detail: T) => void): () => void {
    const listener: EventListener = (evt) => callback((evt as CustomEvent<T>).detail);
    this.addEventListener(event, listener);
    return () => this.removeEventListener(event, listener);
  }

  off(event: string, callback: EventListenerOrEventListenerObject): void {
    this.removeEventListener(event, callback);
  }

  snapshot(): AppState {
    return structuredClone(this._state);
  }
}

export const store = new Store();
